import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Per-page title, description, canonical and share tags for the public site.
// scripts/prerender.mjs saves each public page after this has run, so search
// engines and AI crawlers get these tags (and the page text) without JavaScript.
export const SITE = "https://pravadhgx.pravadhlabs.com";
const IMAGE = `${SITE}/og-image.png`;

export const PUBLIC_PAGES = {
  "/": {
    title: "Pravadh GovernX — CRM, ERP & Finance Data Governance",
    description:
      "Connect Salesforce, Zoho and Oracle, clean and govern the data in one AES-256 layer, and run it under your brand, on your model and your servers.",
  },
  "/features": {
    title: "Data Integration & Governance Features | Pravadh GovernX",
    description:
      "Connect, clean and govern CRM, ERP and finance data: extraction, attachment migration, governance rules, campaigns and EMIE, the built-in AI assistant.",
  },
  "/compare": {
    title: "GovernX vs Admin Panels & Integration Platforms",
    description:
      "How Pravadh GovernX compares with native admin panels and generic integration platforms on governance, data quality, security and cost.",
  },
  "/pricing": {
    title: "Pricing — Data Governance Plans | Pravadh GovernX",
    description:
      "Simple monthly pricing for Pravadh GovernX. Every plan includes a 7-day free trial with no card required. Compare plans and limits.",
  },
  "/security": {
    title: "Security & Trust | Pravadh GovernX",
    description:
      "How Pravadh GovernX protects your data: enterprise-grade encryption, tamper-evident audit trails and full OAuth transparency.",
  },
  "/deploy": {
    title: "Self-Host GovernX on Your Servers | Pravadh GovernX",
    description:
      "Run Pravadh GovernX on any Linux VM, Kubernetes cluster or OCI runtime. Pull private images; you never receive source or copy keys from Pravadh.",
  },
  "/terms": {
    title: "Terms of Service | Pravadh GovernX",
    description: "The terms that govern your use of Pravadh GovernX, including plans, billing, trials and cancellation.",
  },
  "/cookie-policy": {
    title: "Cookie Policy | Pravadh GovernX",
    description: "Which cookies Pravadh GovernX uses, why, and how to change your cookie preferences.",
  },
};

function setTag(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

const meta = (key, isProperty) => () => {
  const el = document.createElement("meta");
  el.setAttribute(isProperty ? "property" : "name", key);
  return el;
};

function apply(page, path, indexable) {
  document.title = page.title;
  const url = `${SITE}${path === "/" ? "/" : path}`;
  setTag('meta[name="description"]', meta("description"), "content", page.description);
  setTag('meta[name="robots"]', meta("robots"), "content", indexable ? "index, follow, max-image-preview:large" : "noindex, nofollow");
  setTag('link[rel="canonical"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    return l;
  }, "href", url);
  const og = {
    "og:type": "website", "og:site_name": "Pravadh GovernX", "og:title": page.title,
    "og:description": page.description, "og:url": url, "og:image": IMAGE,
    "og:image:width": "1200", "og:image:height": "630",
  };
  Object.entries(og).forEach(([k, v]) => setTag(`meta[property="${k}"]`, meta(k, true), "content", v));
  const tw = { "twitter:card": "summary_large_image", "twitter:title": page.title, "twitter:description": page.description, "twitter:image": IMAGE };
  Object.entries(tw).forEach(([k, v]) => setTag(`meta[name="${k}"]`, meta(k), "content", v));
  setJsonLd(indexable ? buildJsonLd(page, path, url) : null);
}

// Structured data so search engines understand what each public page is.
const PUBLISHER = { "@type": "Organization", name: "Pravadh Labs", url: "https://pravadhlabs.com" };
const PAGE_NAMES = {
  "/features": "Features", "/compare": "Compare", "/pricing": "Pricing", "/security": "Security",
  "/deploy": "Self-host", "/terms": "Terms of Service", "/cookie-policy": "Cookie Policy",
};

function buildJsonLd(page, path, url) {
  const website = { "@type": "WebSite", "@id": `${SITE}/#website`, name: "Pravadh GovernX", url: `${SITE}/`, publisher: PUBLISHER };
  if (path === "/") {
    return {
      "@context": "https://schema.org",
      "@graph": [
        website,
        {
          "@type": "SoftwareApplication",
          name: "Pravadh GovernX",
          url: `${SITE}/`,
          description: page.description,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          image: IMAGE,
          publisher: PUBLISHER,
        },
      ],
    };
  }
  return {
    "@context": "https://schema.org",
    "@graph": [
      website,
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: page.title,
        description: page.description,
        isPartOf: { "@id": `${SITE}/#website` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Pravadh GovernX", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: PAGE_NAMES[path] || page.title, item: url },
        ],
      },
    ],
  };
}

function setJsonLd(data) {
  let el = document.head.querySelector('script#seo-jsonld');
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "seo-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.replace(/\/+$/, "") || "/";
    const page = PUBLIC_PAGES[path];
    if (page) apply(page, path, true);
    else apply({ title: "Pravadh GovernX", description: PUBLIC_PAGES["/"].description }, path, false);
  }, [pathname]);
  return null;
}
